import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaPhone, FaWhatsapp, FaClock, FaFacebook, FaInstagram, FaTiktok } from 'react-icons/fa';
import toast from 'react-hot-toast';

const WHATSAPP_NUMBER = '923488477649';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.message) {
      toast.error('Please fill in your name and message');
      return;
    }

    setLoading(true);

    const text = `🔔 *New Inquiry from Waziri Mobile Website*%0A%0A` +
      `👤 *Name:* ${encodeURIComponent(formData.name)}%0A` +
      `📞 *Phone:* ${encodeURIComponent(formData.phone || 'Not provided')}%0A` +
      `📋 *Subject:* ${encodeURIComponent(formData.subject || 'General Inquiry')}%0A%0A` +
      `💬 *Message:*%0A${encodeURIComponent(formData.message)}`;

    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;
    window.open(whatsappUrl, '_blank');

    toast.success('Redirecting you to WhatsApp!');
    setFormData({ name: '', phone: '', subject: '', message: '' });
    setLoading(false);
  };

  const contactInfo = [
    { icon: <FaMapMarkerAlt />, title: 'Visit Us', details: 'Main Branch Ali Plaza, Shop#46, Hussaini Chowk Skardu' },
    { icon: <FaMapMarkerAlt />, title: 'Visit Us', details: '2nd Branch Main bazar Lucky Plaza, shop#1-12, Hussaini Chowk Skardu' },
    { icon: <FaPhone />, title: 'Call Us', details: '+92 348 8477649', link: 'tel:+923488477649' },
    { icon: <FaPhone />, title: 'Call Us', details: '+92 346 4411344', link: 'tel:+923464411344' },
    { icon: <FaWhatsapp />, title: 'WhatsApp Us', details: '+92 348 8477649', link: `https://wa.me/${WHATSAPP_NUMBER}` },
    { icon: <FaClock />, title: 'Business Hours', details: 'Mon-Sat: 10AM - 8PM\nSunday: Closed' },
  ];

  return (
    <div className="py-12">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h1>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Have questions? Reach out to us directly on WhatsApp and we'll respond as soon as possible.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft p-6">
              <h2 className="text-2xl font-bold mb-6">Get in Touch</h2>
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex gap-4"
                  >
                    <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center text-primary-600 text-xl">
                      {info.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">{info.title}</h3>
                      {info.link ? (
                        <a href={info.link} target={info.link.startsWith('https') ? '_blank' : undefined} rel={info.link.startsWith('https') ? 'noopener noreferrer' : undefined} className="text-gray-600 dark:text-gray-400 hover:text-primary-600">
                          {info.details}
                        </a>
                      ) : (
                        <p className="text-gray-600 dark:text-gray-400 whitespace-pre-line">{info.details}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Form - WhatsApp */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft p-6"
            >
              <div className="flex items-center gap-3 mb-6">
                <FaWhatsapp className="text-3xl text-green-500" />
                <h2 className="text-2xl font-bold">Send us a Message on WhatsApp</h2>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Your Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="input-field"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="+92 348 8477649"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Subject</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="How can we help?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="input-field resize-none"
                    placeholder="Please describe your inquiry..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <FaWhatsapp className="text-xl" />
                      Send via WhatsApp
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </div>

        {/* Google Maps */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-12"
        >
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d203.51642788517927!2d75.6342845607764!3d35.29944985515088!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x38e463d8e9f6a6e1%3A0x5b4dfcdd3b63dae1!2sWaziri%20Mobile%20Point!5e0!3m2!1sen!2s!4v1780316784802!5m2!1sen!2s"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              title="Shop Location"
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ContactPage;